// Web Audio engine for MIRRORS — iOS Safari hardened
// ─────────────────────────────────────────────────────────────────────
// A = 432 Hz concert-pitch reference (project-wide).
// Every note→frequency conversion in the app should go through
// `noteToFreq()` below so tuning is consistent across visualizers,
// synth voices, and admin uploads.
// ─────────────────────────────────────────────────────────────────────
export const A4_HZ = 432;
/** Convert a MIDI-style semitone offset from A4 into Hz (A=432). */
export function noteToFreq(semitonesFromA4 = 0) {
  return A4_HZ * Math.pow(2, semitonesFromA4 / 12);
}
/** Convert any 440-based frequency to its 432-based equivalent. */
export function retune432(hz440) {
  return hz440 * (432 / 440);
}

let ctx; let master; let playing = false; let currentTimers = []; let currentNodes = []; let analyser;
let audioUnlocked = false;

function ensureCtx(){
  if(!ctx){
    // Latency-free visual feedback: `interactive` latency hint asks the
    // browser for the smallest possible output buffer, and a small analyser
    // fftSize with zero smoothingTimeConstant means the visualizer sees the
    // *current* audio frame, not a smoothed / delayed average.
    const opts = { sampleRate: 44100, latencyHint: 'interactive' };
    try { ctx = new (window.AudioContext || window.webkitAudioContext)(opts); }
    catch { ctx = new (window.AudioContext || window.webkitAudioContext)(); }
    master = ctx.createGain(); master.gain.value = 0.9; master.connect(ctx.destination);
    analyser = ctx.createAnalyser();
    analyser.fftSize = 256;              // ~5.8 ms at 44.1 kHz — sub-frame latency
    analyser.smoothingTimeConstant = 0;  // no temporal averaging → snap-to-signal
    const tap = ctx.createGain(); tap.gain.value = 1.0; master.connect(tap); tap.connect(analyser);

    // iOS visibility change: suspend/resume to save battery + prevent glitches
    document.addEventListener("visibilitychange", () => {
      if (!ctx) return;
      if (document.hidden && ctx.state === "running") ctx.suspend().catch(() => {});
      else if (!document.hidden && ctx.state === "suspended" && playing) ctx.resume().catch(() => {});
    });
  }
}

// Must be called from a user gesture (click/touchstart) to unlock iOS audio
export function unlockAudio() {
  if (audioUnlocked) return Promise.resolve();
  ensureCtx();
  // Play a silent buffer to unlock
  const buf = ctx.createBuffer(1, 1, 44100);
  const src = ctx.createBufferSource();
  src.buffer = buf; src.connect(ctx.destination); src.start(0);
  return ctx.resume().then(() => { audioUnlocked = true; }).catch(() => { audioUnlocked = true; });
}

export function isAudioUnlocked() { return audioUnlocked; }

// iOS silent switch detection — plays a tiny probe and checks if output was audible
export async function detectSilentSwitch() {
  try {
    ensureCtx();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.001; // inaudible volume
    oscillator.connect(gainNode); gainNode.connect(ctx.destination);
    oscillator.start(); oscillator.stop(ctx.currentTime + 0.05);
    // If AudioContext runs without error, we can't truly detect silent switch from Web Audio alone
    // Return a warning hint instead
    return false; // Can't definitively detect — return false = no warning
  } catch { return false; }
}

function centsToRatio(cents){ return Math.pow(2, cents/1200); }

export function getContext(){ ensureCtx(); return ctx; }
export function getAnalyser(){ ensureCtx(); return analyser; }

export function stopAll(){
  currentTimers.forEach(clearTimeout); currentTimers=[];
  currentNodes.forEach(n=>{try{ if(typeof n.stop === 'function'){ n.stop(); } else if(n && typeof n.disconnect==='function'){ n.disconnect(); } }catch{}});
  currentNodes=[]; playing=false;
}

export function pauseAll(){ ensureCtx(); if(ctx && ctx.state==='running'){ return ctx.suspend(); } return Promise.resolve(); }
export function resumeAll(){ ensureCtx(); if(ctx && ctx.state==='suspended'){ return ctx.resume(); } return Promise.resolve(); }
export function isPlaying(){ return playing; }

export function playBinaural({carrierHz, beatHz, seconds, gain=0.4, offsetCents=0, leftAdjust=0, rightAdjust=0}){
  ensureCtx(); if(ctx.state==='suspended') ctx.resume(); playing = true;
  const now = ctx.currentTime; const ratio = centsToRatio(offsetCents);
  const leftHz = ((carrierHz - beatHz/2) + leftAdjust) * ratio;
  const rightHz = ((carrierHz + beatHz/2) + rightAdjust) * ratio;

  const leftOsc = ctx.createOscillator(); const rightOsc = ctx.createOscillator();
  const leftGain = ctx.createGain(); const rightGain = ctx.createGain(); leftGain.gain.value = 0; rightGain.gain.value = 0;
  const merger = ctx.createChannelMerger(2);
  leftOsc.connect(leftGain); rightOsc.connect(rightGain);
  leftGain.connect(merger, 0, 0); rightGain.connect(merger, 0, 1);
  merger.connect(master);

  leftOsc.frequency.value = leftHz; rightOsc.frequency.value = rightHz;
  const fade = 0.5;
  leftGain.gain.linearRampToValueAtTime(0, now); rightGain.gain.linearRampToValueAtTime(0, now);
  leftGain.gain.linearRampToValueAtTime(gain, now + fade); rightGain.gain.linearRampToValueAtTime(gain, now + fade);
  leftOsc.start(); rightOsc.start();
  const stopAt = now + seconds;
  leftGain.gain.linearRampToValueAtTime(0.0001, stopAt); rightGain.gain.linearRampToValueAtTime(0.0001, stopAt);
  const stopNode = () => { try{leftOsc.stop(); rightOsc.stop();}catch{} };
  currentNodes.push({stop: stopNode}); setTimeout(stopNode, seconds*1000 + 100);
  return {leftOsc, rightOsc, leftGain, rightGain, ctx, stop: stopNode};
}

export function playIsochronic({carrierHz, beatHz, seconds, gain=0.4, duty=0.5}){
  ensureCtx(); if(ctx.state==='suspended') ctx.resume(); playing = true;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator(); const g = ctx.createGain(); g.gain.value = 0;
  osc.frequency.value = carrierHz; osc.connect(g); g.connect(master);
  const period = 1/beatHz; let t = 0; while(t < seconds){
    const onStart = now + t; const onEnd = onStart + period*duty; const offEnd = onStart + period;
    g.gain.setValueAtTime(0, onStart); g.gain.linearRampToValueAtTime(gain, onStart + 0.02);
    g.gain.setValueAtTime(gain, onEnd); g.gain.linearRampToValueAtTime(0.0001, onEnd + 0.02);
    t += period;
  }
  osc.start(); const stopAt = now + seconds; const stopper = () => { try{osc.stop();}catch{} };
  currentNodes.push({stop: stopper}); setTimeout(stopper, seconds*1000 + 100);
}

export function playMono({freq=432, seconds=60, gain=0.2, type='sine'}){
  ensureCtx(); if(ctx.state==='suspended') ctx.resume(); playing = true;
  const osc = ctx.createOscillator(); osc.type = type; const g = ctx.createGain(); g.gain.value = gain; osc.frequency.value = freq; osc.connect(g); g.connect(master);
  const now = ctx.currentTime; osc.start(); const stopper = () => { try{g.gain.exponentialRampToValueAtTime(0.0001, now + seconds - 0.05); osc.stop(now + seconds);}catch{} };
  currentNodes.push({stop: stopper}); setTimeout(stopper, seconds*1000 + 100);
}

export function playStack({phases, calibrationCents=0}){
  ensureCtx(); if(ctx.state==='suspended') ctx.resume(); stopAll(); playing=true;
  let t = 0; phases.forEach((p)=>{ const seconds = Math.max(5, (p.minutes||1)*60); const timer = setTimeout(()=>{ playBinaural({carrierHz: p.carrier_hz, beatHz: p.beat_hz, seconds, gain:0.35, offsetCents: calibrationCents}); }, t*1000); currentTimers.push(timer); t += seconds; });
  const endTimer = setTimeout(()=>{ playing=false; }, t*1000 + 200); currentTimers.push(endTimer);
}

export function playTone({freq=432, gain=0.2, pan=0, type='sine'}){
  ensureCtx(); if(ctx.state==='suspended') ctx.resume(); playing = true;
  const osc = ctx.createOscillator(); osc.type = type; const g = ctx.createGain(); g.gain.value = gain; const p = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
  osc.frequency.value = freq; osc.connect(g); if(p){ p.pan.value = pan; g.connect(p); p.connect(master);} else { g.connect(master); }
  osc.start(); const stopper = () => { try{ g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.05); osc.stop(ctx.currentTime + 0.06);}catch{} };
  currentNodes.push({stop: stopper, disconnect: ()=>{ try{osc.disconnect(); g.disconnect(); if(p) p.disconnect();}catch{}}});
  return {osc, gainNode: g, panner: p, ctx, stop: stopper};
}

export function getMasterGain(){ ensureCtx(); return master; }

/* Persistent oscillator with smooth frequency gliding — zero pops */
export function createSmoothTone({ freq = 432, gain = 0.15, type = 'sine' } = {}) {
  ensureCtx();
  if (ctx.state === 'suspended') ctx.resume();
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;
  const g = ctx.createGain();
  g.gain.value = 0.0001;
  const p = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
  osc.connect(g);
  if (p) { g.connect(p); p.connect(master); } else { g.connect(master); }
  osc.start();
  let alive = true;
  const node = {
    setFrequency(hz) {
      if (!alive) return;
      osc.frequency.setTargetAtTime(hz, ctx.currentTime, 0.03);
    },
    setGain(val) {
      if (!alive) return;
      g.gain.setTargetAtTime(Math.max(0.0001, val), ctx.currentTime, 0.02);
    },
    fadeIn(targetGain = gain) {
      if (!alive) return;
      g.gain.cancelScheduledValues(ctx.currentTime);
      g.gain.setTargetAtTime(Math.max(0.0001, targetGain), ctx.currentTime, 0.05);
    },
    fadeOut() {
      if (!alive) return;
      g.gain.cancelScheduledValues(ctx.currentTime);
      g.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.06);
    },
    destroy() {
      if (!alive) return;
      alive = false;
      g.gain.cancelScheduledValues(ctx.currentTime);
      g.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.02);
      setTimeout(() => {
        try { osc.stop(); } catch (e) { /* AudioNode already stopped */ }
        try { osc.disconnect(); g.disconnect(); if (p) p.disconnect(); } catch (e) { /* Already disconnected */ }
      }, 150);
    },
    get isAlive() { return alive; },
  };
  currentNodes.push({ stop: () => node.destroy() });
  return node;
}


export function playHyperbeam({ seconds = 600, gain = 0.3, carrierHz = 40000, modulatorHz = 528 }) {
  ensureCtx(); if (ctx.state === 'suspended') ctx.resume(); playing = true;
  const now = ctx.currentTime;

  // LEFT: 40kHz carrier AM'd by 528Hz — signal = carrier * (mod*0.5 + 0.5) * gain
  const leftCarrier = ctx.createOscillator();
  leftCarrier.frequency.value = carrierHz;
  const leftAM = ctx.createGain();
  leftAM.gain.value = 0.5; // DC offset
  const leftMod = ctx.createOscillator();
  leftMod.frequency.value = modulatorHz;
  const leftModDepth = ctx.createGain();
  leftModDepth.gain.value = 0.5;
  leftMod.connect(leftModDepth);
  leftModDepth.connect(leftAM.gain); // gain = 0.5 ± 0.5 → [0,1]
  leftCarrier.connect(leftAM);

  // RIGHT: 40000.5Hz carrier (0.5Hz offset for stereo beam feel)
  const rightCarrier = ctx.createOscillator();
  rightCarrier.frequency.value = carrierHz + 0.5;
  const rightAM = ctx.createGain();
  rightAM.gain.value = 0.5;
  const rightMod = ctx.createOscillator();
  rightMod.frequency.value = modulatorHz;
  const rightModDepth = ctx.createGain();
  rightModDepth.gain.value = 0.5;
  rightMod.connect(rightModDepth);
  rightModDepth.connect(rightAM.gain);
  rightCarrier.connect(rightAM);

  // Master output with fade-in
  const leftOut = ctx.createGain(); leftOut.gain.value = 0;
  const rightOut = ctx.createGain(); rightOut.gain.value = 0;
  leftAM.connect(leftOut); rightAM.connect(rightOut);

  const merger = ctx.createChannelMerger(2);
  leftOut.connect(merger, 0, 0); rightOut.connect(merger, 0, 1);
  merger.connect(master);

  leftOut.gain.linearRampToValueAtTime(gain, now + 0.5);
  rightOut.gain.linearRampToValueAtTime(gain, now + 0.5);
  leftCarrier.start(); leftMod.start(); rightCarrier.start(); rightMod.start();

  const stopper = () => {
    try {
      const t = ctx.currentTime;
      leftOut.gain.linearRampToValueAtTime(0.0001, t + 0.1);
      rightOut.gain.linearRampToValueAtTime(0.0001, t + 0.1);
      setTimeout(() => { try { leftCarrier.stop(); leftMod.stop(); rightCarrier.stop(); rightMod.stop(); } catch (e) { /* Oscillators already stopped */ } }, 200);
    } catch (e) { console.error("[AudioEngine] Hyperbeam stop error:", e); }
  };
  currentNodes.push({ stop: stopper });
  if (seconds > 0) setTimeout(stopper, seconds * 1000 + 100);
  return { stop: stopper };
}
